import React, { useMemo, useState, useEffect } from "react";
import {
  Table,
  Flex,
  useDisclosure,
  TableContainer,
  HStack,
  Tooltip,
  IconButton,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiEdit2Fill } from "react-icons/ri";
import { FaTrash } from "react-icons/fa";
import Card from "components/Card/Card.js";
import { truncate } from "lib/utils";
import { dynamicSort } from "utils/helpers";
import DeleteConfirmationModal from "components/DeleteConfirmationModal";
import Header from "components/ui/table/Header";
import Body from "components/ui/table/Body";
import Pagination from "components/ui/table/Pagination";
import { Search } from "components/ui/table/Search";
import { FaPlus } from "react-icons/fa6";
import { CreatedAt } from "components/CellElements";
import { Basic } from "components/CellElements";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import useDataTable from "hooks/useDataTable";
import { useGetPaginatedArticles, useDeleteArticle } from "queries/useArticle";
import { Title } from "components/CellElements";

function Articles() {
  const { formMethods, table, deleteModal, navigate, bgButton } = useArticles();

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <HStack spacing={4} mb={4} mx="6">
          <Search methods={formMethods} />
          <Tooltip label="Add New Article" placement="top">
            <IconButton
              aria-label="add new article"
              icon={<Icon as={FaPlus} boxSize={4} />}
              colorScheme="primary"
              bg={bgButton}
              onClick={() => navigate("/articles/create")}
            />
          </Tooltip>
        </HStack>
        <TableContainer>
          <Table {...table.getTableProps()}>
            <Header {...table.header} />
            <Body {...table.body} />
          </Table>
        </TableContainer>
        <Pagination mt="10" {...table.pagination} />
        <DeleteConfirmationModal {...deleteModal} />
      </Card>
    </Flex>
  );
}

export default Articles;

const useArticles = () => {
  const navigate = useNavigate();
  const deleteModal = useDisclosure();
  const formMethods = useForm({
    defaultValues: {
      search: localStorage.getItem("articlesSearch") || "",
      league: localStorage.getItem("articlesLeague") || "",
    },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const savedLimit = localStorage.getItem("articlesLimit");
    return savedLimit ? parseInt(savedLimit, 10) : 50;
  });
  const [currentSort, setCurrentSort] = useState([["createdAt", "DESC"]]);
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const watchSearch = formMethods?.watch("search");

  const getArticles = useGetPaginatedArticles({
    page: currentPage,
    limit,
    search: watchSearch,
    order: currentSort,
  });

  console.log(getArticles.data);
  const deleteArticle = useDeleteArticle({
    onSuccess: () => deleteModal.onClose(),
  });

  const onSort = dynamicSort(["createdAt"]);
  const data = useMemo(() => getArticles.data?.data?.articles || [], [
    JSON.stringify(getArticles.data),
  ]);

  const onDelete = (article) => {
    setSelectedArticleId(article?.id);
    deleteModal.onOpen();
  };

  const onEdit = (article) => navigate(`/articles/view/${article?.id}`);

  const columns = useMemo(
    () => [
      {
        Header: "Title",
        accessor: "title",
        Cell: ({ value }) => <Title title={`${value}`} />,
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ value }) => <Basic text={truncate(value)} />,
      },
      {
        Header: "Tags",
        accessor: "tags",
        Cell: ({ value }) => <Basic text={`${value}`} />,
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => <CreatedAt date={value} />,
      },
      {
        accessor: "id",
        disableSortBy: true,
        Cell: ({ row }) => (
          <HStack spacing={3}>
            <Tooltip label="View" placement="top">
              <IconButton
                aria-label="view league contact"
                icon={<Icon as={RiEdit2Fill} boxSize={5} />}
                onClick={() => onEdit(row.original)}
              />
            </Tooltip>
            <Tooltip label="Delete" placement="top">
              <IconButton
                aria-label="delete league contact"
                icon={<Icon as={FaTrash} boxSize={4} />}
                colorScheme="red"
                onClick={() => onDelete(row.original)}
              />
            </Tooltip>
          </HStack>
        ),
      },
    ],
    []
  );

  const sortTable = useMemo(() => {
    return currentSort.map(([column, direction]) => {
      let id = column?.replace("name", "firstName") || "createdAt";
      let desc = direction === "ASC" ? false : true;
      return { id, desc };
    });
  }, [currentSort]);

  const pageCount = useMemo(() => getArticles.data?.data?.totalPages || 0, [
    getArticles.data,
  ]);

  const table = useDataTable({
    desktopHiddenColumns: [],
    mobileHiddenColumns: [],
    data,
    columns,
    initialState: {
      pageCount,
      pageIndex: currentPage - 1,
      pageSize: limit,
      sortBy: sortTable,
    },
    setter: { setCurrentPage, setLimit },
    isLoading: getArticles.isLoading,
  });

  useEffect(() => {
    getArticles.refetch({
      page: table.pagination.pageIndex,
      limit: table.pagination.pageSize,
      order: currentSort,
      search: watchSearch,
    });

    localStorage.setItem("articlesPage", table.pagination.pageIndex.toString());
    localStorage.setItem("articlesLimit", table.pagination.pageSize.toString());

    if (watchSearch !== undefined) {
      localStorage.setItem("articlesSearch", watchSearch || "");
    }
  }, [
    table.pagination.pageIndex,
    table.pagination.pageSize,
    watchSearch,
    JSON.stringify(table.sort?.sortBy),
  ]);

  useEffect(() => {
    let sort = table.sort?.sortBy?.map((s) => {
      let id = s.id?.replace("name", "firstName") || "createdAt";
      let desc = s.desc == false ? "ASC" : "DESC";
      return [id, desc];
    });

    setCurrentSort(sort);
  }, [JSON.stringify(table.sort?.sortBy)]);

  const bgButton = useColorModeValue(
    "linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)",
    "gray.800"
  );

  return {
    bgButton,
    formMethods,
    table,
    navigate,
    deleteModal: {
      ...deleteModal,
      onConfirmDelete: () => deleteArticle.mutate(selectedArticleId),
      isLoading: deleteArticle.isLoading,
      title: "Are you sure you want to delete this article?",
    },
  };
};
